import * as PIXI from "pixi.js"
import { createBackground, createGrid, createHomeDot, createLassoSquare } from "./renderers"
import { isDesktopApp } from "../services/desktop"

export type PixiSceneOptions = {
  dom_element: HTMLElement
  app_width: number
  app_height: number
  is_dark_mode: boolean
}

export type PixiScene = {
  app: PIXI.Application
  is_dark_mode: boolean
  layers: PixiLayers
  pointer_square: PIXI.Graphics
  // Draws the stage on the next frame. Nothing redraws it otherwise, so call
  // this after every change to it.
  request_render: () => void
  cancel_render: () => void
}

export type PixiLayers = {
  background: PIXI.Graphics
  grid: PIXI.Graphics
  connections: PIXI.Graphics | null
  ideas: PIXI.Graphics | null
  full_titles: PIXI.Container | null
  hover: PIXI.Container
  opened: PIXI.Graphics
  drag: PIXI.Container
  selection: PIXI.Graphics
  lasso: PIXI.Graphics
  home: PIXI.Graphics
}

const nonrenderable = <T extends PIXI.DisplayObject>(object: T): T => {
  object.renderable = false
  return object
}

export const create_pixi_scene = ({
  dom_element,
  app_width,
  app_height,
  is_dark_mode,
}: PixiSceneOptions): PixiScene => {
  // By default Pixi redraws the whole canvas every display frame, even when
  // nothing has changed, which keeps the GPU and the window compositor busy for
  // as long as the Mosaic is on screen. Draw only when asked to instead.
  const app = new PIXI.Application({
    width: app_width,
    height: app_height,
    resolution: window.devicePixelRatio,
    autoDensity: true,
    antialias: true,
    resizeTo: dom_element,
    autoStart: false,
  })

  let render_frame: number | null = null
  const request_render = () => {
    if (render_frame != null) return
    render_frame = window.requestAnimationFrame(() => {
      render_frame = null
      app.render()
    })
  }
  const cancel_render = () => {
    if (render_frame != null) window.cancelAnimationFrame(render_frame)
    render_frame = null
  }

  // Desktop apps show the arrow over ideas, not the hand.
  if (isDesktopApp()) app.renderer.events.cursorStyles.pointer = "default"
  const layers: PixiLayers = {
    background: createBackground(is_dark_mode, app_width, app_height),
    grid: createGrid(is_dark_mode),
    connections: null,
    ideas: null,
    full_titles: null,
    hover: nonrenderable(new PIXI.Container()),
    opened: nonrenderable(new PIXI.Graphics()),
    drag: nonrenderable(new PIXI.Container()),
    selection: nonrenderable(new PIXI.Graphics()),
    lasso: createLassoSquare(),
    home: createHomeDot(is_dark_mode),
  }
  const scene = {
    app,
    is_dark_mode,
    layers,
    pointer_square: new PIXI.Graphics(),
    request_render,
    cancel_render,
  }

  scene.layers.hover.addChild(scene.pointer_square)

  app.stage.addChild(scene.layers.background)
  app.stage.addChild(scene.layers.grid)
  app.stage.addChild(scene.layers.hover)
  app.stage.addChild(scene.layers.opened)
  app.stage.addChild(scene.layers.drag)
  app.stage.addChild(scene.layers.selection)
  app.stage.addChild(scene.layers.lasso)
  app.stage.addChild(scene.layers.home)

  app.stage.interactive = true
  app.stage.x = app_width / 4
  app.stage.y = app_height / 3
  request_render()

  return scene
}
